/**
 * One unit as a list row (Units redesign, Slice 6).
 *
 * REPLACES A THIRTEEN-COLUMN TABLE, AND DOES NOT IMITATE IT. The co-living
 * table carried Unit ID, Ort, PLZ, Adresse, Verwaltung, Status, three occupancy
 * counts, four money columns and a lease start, inside an `overflow-x-auto`
 * wrapper — so reading one unit meant scrolling sideways. This condenses the
 * same unit into zones that fit the screen: who it is, how big it is, how full
 * it is, what it earns, what it costs, and a way in.
 *
 * SEGMENT-AGNOSTIC ON PURPOSE, so Slice 7 can reuse it for business apartments.
 * Nothing here knows what co-living is: the occupancy block renders only when
 * occupancy numbers are passed, and the view model already returns null for
 * those on a business apartment (one contract, not a per-room split). That is
 * why the block is driven by the data rather than by a `segment` flag.
 *
 * ONE OPTIONAL ZONE, OFF BY DEFAULT. `showAvailableFrom` adds the letting
 * date, which the business-apartment table carried and co-living's did not. It
 * is a prop rather than something derived from the data because the default has
 * to keep co-living's row exactly as Slice 6 left it: a row that grew a column
 * because a field happened to be present would be a silent change to a shipped
 * surface. It also lands where co-living shows occupancy, so neither segment
 * leaves a hole in the middle of the row.
 *
 * THE MEDIA SLOT IS NOT A PHOTO, AND DOES NOT PRETEND TO BE. There is no unit
 * image in the loaded data and this slice adds no request for one, so the slot
 * is a themed icon tile. It is sized and positioned like the thumbnail it will
 * become, so swapping in a real image later is a change of content, not layout.
 */
import React from "react";
import { Link } from "react-router-dom";
import { Building2, ChevronRight } from "lucide-react";

import { occupancyStatusBadgeTone } from "../../../utils/unitOccupancyStatus";
import { formatDateOnly } from "../../../utils/datetime";
import { formatCurrency } from "./apartmentsHelpers";

/**
 * Status colours, theme-aware.
 *
 * The SEMANTICS are not redefined here — the tone comes from
 * `occupancyStatusBadgeTone`, the helper that already owns which status reads
 * as which colour. Only the classes are new, because the existing class helper
 * is written in dark-only hex (`text-[#3ddc84]` on `rgba(61,220,132,0.1)`),
 * which on a white card is pale green on near-white. Tailwind palette steps
 * with a dark counterpart each, so the badge follows the theme.
 */
const BADGE_TONE_CLASSES = {
  green:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  orange:
    "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
  blue: "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300",
  slate: "bg-muted text-muted-foreground dark:bg-white/[0.06]",
};

/** Occupancy bar segment colours, matching the badge tones. */
const BAR_CLASSES = {
  occupied: "bg-emerald-500 dark:bg-emerald-400",
  reserved: "bg-sky-500 dark:bg-sky-400",
};

/** A money value, or an em dash — never a zero standing in for a missing one. */
function Money({ value }) {
  if (value == null) return <span className="text-muted-foreground">—</span>;
  return <span className="tabular-nums">{formatCurrency(value)}</span>;
}

/**
 * A labelled figure. The label is what makes the row readable without a header.
 *
 * `data-stat` is a deliberate test seam. CI runs a Babel plugin that wraps
 * dynamic JSX expressions in extra elements (CLAUDE.md §8.4), which breaks
 * text-based lookups in ways that pass locally and fail there — "60.5" and
 * " m²" land in different elements, and a lone value matches twice. A stable
 * attribute is immune to all of it.
 */
function Stat({ label, children, className = "" }) {
  return (
    <div className={`min-w-0 ${className}`} data-stat={label}>
      <div className="text-[10px] font-medium uppercase tracking-[0.04em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 truncate text-[12.5px] text-foreground">{children}</div>
    </div>
  );
}

/**
 * How full the unit is.
 *
 * A proportional bar plus "4 / 7", NOT one icon per room: a 25-room unit would
 * otherwise render 25 glyphs and wreck the row. The bar is bounded whatever the
 * room count, and the exact figures stay legible as text. Reserved is kept
 * visually distinct from occupied, because a reserved room is spoken for but
 * nobody has moved in.
 */
function OccupancyBlock({ occupied, reserved, free, total }) {
  const totalRooms = Number(total) || 0;
  const pct = (n) => (totalRooms > 0 ? `${(Number(n || 0) / totalRooms) * 100}%` : "0%");

  return (
    <Stat label="Belegung">
      <div className="flex items-center gap-2">
        <span className="tabular-nums">
          {occupied} <span className="text-muted-foreground">/ {totalRooms}</span>
        </span>
        <span
          className="flex h-1.5 w-14 shrink-0 overflow-hidden rounded-full bg-muted"
          title={`Belegt ${occupied} · Reserviert ${reserved} · Frei ${free}`}
          aria-label={`Belegt ${occupied}, Reserviert ${reserved}, Frei ${free}`}
        >
          <span className={BAR_CLASSES.occupied} style={{ width: pct(occupied) }} />
          <span className={BAR_CLASSES.reserved} style={{ width: pct(reserved) }} />
        </span>
      </div>
    </Stat>
  );
}

export default function UnitListRow({ item, showAvailableFrom = false }) {
  if (!item) return null;

  const {
    id,
    displayId,
    addressLine1,
    addressLine2,
    roomCount,
    totalAreaM2,
    occupancyStatus,
    occupancyLabel,
    occupiedCount,
    reservedCount,
    freeCount,
    totalRooms,
    currentRevenueChf,
    runningCostsMonthlyChf,
    availableFrom,
  } = item;

  // Occupancy numbers are absent for a business apartment, so the block simply
  // does not appear rather than rendering a 0/0 that reads like an empty
  // co-living unit.
  const hasOccupancyCounts = occupiedCount != null && totalRooms != null;

  return (
    <div className="flex flex-col gap-3 rounded-[12px] border border-border bg-card px-4 py-3 transition-colors hover:border-foreground/20 xl:flex-row xl:items-center xl:gap-4">
      {/* Zone A — identity */}
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-muted text-muted-foreground"
          aria-hidden="true"
        >
          <Building2 className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          {/*
            The focus ring is explicit because every other control on this page
            carries it and this link was the one exception: it fell back to the
            user-agent outline, which is a different colour and offset from the
            ring the CTA, both selects, the tabs, the open link and the pager all
            use. Focus was visible either way — this is consistency, not a fix.
          */}
          <Link
            to={`/admin/units/${id}`}
            className="block truncate rounded-[4px] font-mono text-[13px] font-semibold text-foreground hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {displayId}
          </Link>
          <div className="truncate text-[12px] text-muted-foreground" title={addressLine1}>
            {addressLine1 || "—"}
          </div>
          <div className="truncate text-[11.5px] text-muted-foreground">
            {addressLine2}
          </div>
        </div>
      </div>

      {/*
        Zones B-E — the figures. They wrap before they ever push the row wide.

        THE ROW GOES HORIZONTAL AT `lg`, NOT `sm`, AND THE FIGURES STOP WRAPPING
        THERE TOO. The six slots have fixed widths summing to 632px plus 100px of
        gaps: 732px before the identity zone and the open link get a pixel. From
        `sm` (640px) the old classes both turned the row horizontal AND forced the
        figures onto one line, which needs roughly 990px — so between 640 and
        1023 the row pushed the page sideways by up to 241px, in both themes. That
        is the one thing this redesign set out to remove, and it stayed invisible
        because the earlier passes measured 390, 1024 and 1440, all three of which
        are fine.

        Letting only the figures wrap is NOT enough: the identity zone is
        `flex-1 min-w-0`, so in a horizontal row it collapses to nothing and the
        wrapped figures render straight over the unit id and address. Below the
        switch the row therefore keeps the stacked shape it already uses on a
        phone — identity, then figures, then the link — which is the layout this
        list has been shipping at 390px all along.

        AND THE SWITCH IS `xl`, NOT `lg`, BECAUSE FITTING IS NOT THE SAME AS
        BEING READABLE. At 1024 the figures do fit on one line, but only by
        leaving the identity zone about 48px of text: `CL-001` rendered as
        `CL-0…` and `Musterweg 1` as `Muste…`. A row whose own id is elided is
        worse than a row that takes two lines, and the id is what every other
        column is about. At 1280 the identity keeps ~360px and nothing truncates.
        This squeeze predates the breakpoint work — it was there whenever the
        one-line form was active at 1024 — so it is fixed here rather than
        carried forward.
      */}
      <div className="flex flex-wrap items-start gap-x-5 gap-y-3 xl:flex-nowrap xl:items-center">
        <Stat label="Zimmer" className="w-[72px]">
          <span className="tabular-nums">{roomCount > 0 ? roomCount : "—"}</span>
        </Stat>

        {/* Fail-closed: a partial sum rendered as an exact figure would be a
            lie the reader cannot detect. See computeTotalAreaM2. */}
        <Stat label="Fläche" className="w-[88px]">
          {totalAreaM2 == null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className="tabular-nums">{totalAreaM2} m²</span>
          )}
        </Stat>

        {hasOccupancyCounts ? (
          <div className="w-[128px]">
            <OccupancyBlock
              occupied={occupiedCount}
              reserved={reservedCount}
              free={freeCount}
              total={totalRooms}
            />
          </div>
        ) : null}

        {showAvailableFrom ? (
          <Stat label="Verfügbar ab" className="w-[112px]">
            {/* `formatDateOnly` already answers a missing date with an em dash,
                so an absent letting date reads the same as every other unknown
                in this row. */}
            <span className="tabular-nums">{formatDateOnly(availableFrom)}</span>
          </Stat>
        ) : null}

        <Stat label="Status" className="w-[104px]">
          {occupancyStatus == null ? (
            // Unknown is NOT free. `occupancyStatusBadgeTone` gives both the
            // same slate tone, so the distinction has to be made here: no
            // badge at all, rather than one that reads as a confident answer.
            <span className="text-muted-foreground">—</span>
          ) : (
            <span
              className={`inline-block rounded-full px-2 py-[2px] text-[10.5px] font-semibold ${
                BADGE_TONE_CLASSES[occupancyStatusBadgeTone(occupancyStatus)] ||
                BADGE_TONE_CLASSES.slate
              }`}
            >
              {occupancyLabel}
            </span>
          )}
        </Stat>

        <Stat label="Einnahmen" className="w-[116px]">
          <Money value={currentRevenueChf} />
        </Stat>

        {/*
          WHAT THE UNIT COSTS, NOT WHAT ITS LANDLORD INVOICES. The slot is the
          same one Slice 6 shipped -- same position, same 124px, same `Money`
          path -- only the source changed: `landlord_rent_monthly_chf` had lost
          its write path and read 0 for every unit, so this column was printing
          a zero as though it had measured one. `runningCostsMonthlyChf` (F2a)
          is the recurring monthly run-rate over `unit_costs` plus the landlord
          deposit insurance premium.

          ONE FIGURE, NO BREAKDOWN. F2a also returns per-bucket amounts and
          data-quality counters, and neither is rendered here: a row that has to
          explain itself is a row that has stopped being scannable, and the
          reasons belong on the unit's own page. For a unit with no cost basis
          at all, the em dash `Money` already renders for a null is the whole
          story this row tells.
        */}
        <Stat label="Laufende Kosten" className="w-[124px]">
          <Money value={runningCostsMonthlyChf} />
        </Stat>
      </div>

      {/* Zone F — the only action this list has ever offered. `onEdit`/`onDelete`
          reach the old tables and are rendered by neither, so showing them here
          would be a new capability, not a redesign. */}
      <Link
        to={`/admin/units/${id}`}
        aria-label={`${displayId} öffnen`}
        className="inline-flex shrink-0 items-center gap-1 self-start rounded-[8px] border border-border px-2.5 py-1.5 text-[12px] text-muted-foreground transition-colors hover:border-foreground/20 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring xl:self-center"
      >
        Öffnen
        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
      </Link>
    </div>
  );
}
